import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { reportsApi, assessmentApi, settingsApi, oesApi } from '../../services/api';
import { useInstanceMode } from '../../providers/InstanceModeProvider';
import { useLabels } from '../../providers/LabelsProvider';
import Layout from '../../components/layout/Layout';
import { Card } from '../../components/common/Card';
import CustomSelect from '../../components/common/CustomSelect';
import generateReportPdf from '../../utils/generateReportPdf';
import generateFullReportPdf from '../../utils/generateFullReportPdf';
import anonymizeReport from '../../utils/anonymizeReport';
import DependencyMap from '../../components/DependencyMap';
import { useT } from '../../providers/I18nProvider';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart as RechartsBar,
  Bar,
  RadarChart as RechartsRadar,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ─── Constants ───────────────────────────────────────────────────────
// Tab labels are resolved inside the Reports component using t()
const BASE_TAB_KEYS = ['executive', 'criticality', 'posture', 'risk', 'comparative'];

const CRITICALITY_COLORS = {
  MA: '#dc2626', A: '#f97316', M: '#eab308', B: '#3b82f6', MB: '#22c55e',
};
const CRITICALITY_LABELS = {
  MA: 'Very High', A: 'High', M: 'Moderate', B: 'Low', MB: 'Very Low',
};
const RISK_COLORS = {
  Critical: '#dc2626', High: '#f97316', Moderate: '#eab308', Low: '#3b82f6', Info: '#94a3b8', Unknown: '#cbd5e1',
};
const COMPLIANCE_COLORS = ['#dc2626', '#f97316', '#3b82f6', '#22c55e'];
const FUNCTION_COLORS = {
  Govern: '#6366f1', Identify: '#3b82f6', Protect: '#22c55e', Detect: '#eab308', Respond: '#f97316', Recover: '#dc2626',
};
const PIE_COLORS = ['#3b82f6', '#6366f1', '#22c55e', '#f97316', '#dc2626', '#eab308', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b'];

// ─── Utility helpers ────────────────────────────────────────────────
function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function StatCard({ label, value, sub, color = 'text-eucnet-navy' }) {
  return (
    <Card className="p-5 flex flex-col items-center text-center">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </Card>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold text-slate-800">{title}</h3>
      {children}
    </div>
  );
}

function ExportButton({ onClick, loading, label }) {
  const t = useT();
  const displayLabel = label || t('common.exportCsv');
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      {loading ? t('admin.exporting') : displayLabel}
    </button>
  );
}

// ─── Tab panels ──────────────────────────────────────────────────────

function ExecutiveSummary({ data }) {
  const labels = useLabels();
  const t = useT();
  const statusData = useMemo(() => {
    const labels = { draft: 'Draft', active: 'Active', paused: 'Paused', completed: 'Completed', archived: 'Archived' };
    const colors = ['#94a3b8', '#3b82f6', '#eab308', '#22c55e', '#64748b'];
    return Object.entries(data.assessment_status_counts || {})
      .filter(([, v]) => v > 0)
      .map(([k, v], i) => ({ name: labels[k] || k, value: v, color: colors[i % colors.length] }));
  }, [data]);

  const sectorData = useMemo(() =>
    (data.sector_distribution || []).map((s, i) => ({ ...s, name: s.sector, value: s.count, color: PIE_COLORS[i % PIE_COLORS.length] })),
    [data]
  );

  const critData = useMemo(() =>
    (data.criticality_distribution || []).map(d => ({
      name: CRITICALITY_LABELS[d.level] || d.level,
      value: d.count,
      color: CRITICALITY_COLORS[d.level] || '#94a3b8',
    })),
    [data]
  );

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('admin.totalAssessments')} value={data.total_assessments || 0} />
        <StatCard label={t('dashboard.entityProfiled', { entity: labels.entityName })} value={data.total_oes || 0} />
        <StatCard label={t('dashboard.avgCompliance')} value={`${data.average_compliance || 0}%`} color={data.average_compliance >= 60 ? 'text-green-600' : 'text-orange-500'} />
        <StatCard label={t('admin.maxRiskScore')} value={data.max_risk_score || 0} color={data.max_risk_score >= 15 ? 'text-red-600' : data.max_risk_score >= 10 ? 'text-orange-500' : 'text-green-600'} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('dashboard.assessmentStatus')}</h4>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                  {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </Card>

        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('dashboard.sectorDistribution')}</h4>
          {sectorData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie data={sectorData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                  {sectorData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </Card>

        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('dashboard.criticalityDistribution')}</h4>
          {critData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RechartsBar data={critData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" name={`${labels.entityName} Count`} radius={[0, 4, 4, 0]}>
                  {critData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </RechartsBar>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </Card>
      </div>

      {/* Risk overview row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('admin.complianceDistribution')}</h4>
          {(data.compliance_distribution || []).some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie data={(data.compliance_distribution || []).filter(d => d.count > 0).map((d, i) => ({ name: d.band, value: d.count, color: COMPLIANCE_COLORS[i] }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                  {(data.compliance_distribution || []).filter(d => d.count > 0).map((_, i) => <Cell key={i} fill={COMPLIANCE_COLORS[i % COMPLIANCE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </Card>

        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('admin.riskClassification')}</h4>
          {(data.risk_distribution || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RechartsBar data={(data.risk_distribution || []).map(d => ({ name: d.classification, value: d.count, color: RISK_COLORS[d.classification] || '#94a3b8' }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                  {(data.risk_distribution || []).map((d, i) => <Cell key={i} fill={RISK_COLORS[d.classification] || '#94a3b8'} />)}
                </Bar>
              </RechartsBar>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </Card>
      </div>
    </div>
  );
}


function CriticalityReport({ data, onExport, exporting }) {
  const labels = useLabels();
  const t = useT();
  const rows = data.oes_criticality_rows || [];

  return (
    <div className="space-y-6">
      <SectionHeader title={`${labels.entityName} Criticality Assessment`}>
        <ExportButton onClick={() => onExport('criticality')} loading={exporting} />
      </SectionHeader>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={`${t('common.total')} ${labels.entityName}`} value={data.total_oes || 0} />
        <StatCard label={t('admin.assessed')} value={data.assessed_criticality || 0} />
        <StatCard label={t('common.pending')} value={(data.total_oes || 0) - (data.assessed_criticality || 0)} color="text-orange-500" />
        <StatCard label={t('admin.completion')} value={data.total_oes ? `${Math.round((data.assessed_criticality / data.total_oes) * 100)}%` : '0%'} />
      </div>

      {/* Distribution chart */}
      <Card className="p-5">
        <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('admin.criticalityLevelDist')}</h4>
        {(data.criticality_distribution || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <RechartsBar data={(data.criticality_distribution || []).map(d => ({
              name: CRITICALITY_LABELS[d.level] || d.level,
              count: d.count,
              color: CRITICALITY_COLORS[d.level] || '#94a3b8',
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name={`${labels.entityName} Count`} radius={[4, 4, 0, 0]}>
                {(data.criticality_distribution || []).map((d, i) => (
                  <Cell key={i} fill={CRITICALITY_COLORS[d.level] || '#94a3b8'} />
                ))}
              </Bar>
            </RechartsBar>
          </ResponsiveContainer>
        ) : <p className="text-sm text-slate-400 text-center py-8">No criticality assessments completed yet</p>}
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{labels.entityName} Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sector</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Criticality</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Impact</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Automation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No {labels.entityName} data available</td></tr>
              ) : rows.map((row) => (
                <tr key={row.oes_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.oes_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{row.sector}</td>
                  <td className="px-4 py-3 text-center">
                    {row.criticality ? (
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: CRITICALITY_COLORS[row.criticality?.toUpperCase()] || '#94a3b8' }}>
                        {CRITICALITY_LABELS[row.criticality?.toUpperCase()] || row.criticality}
                      </span>
                    ) : <span className="text-xs text-gray-400">Pending</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">{row.impact_level ? (CRITICALITY_LABELS[row.impact_level?.toUpperCase()] || row.impact_level) : '—'}</td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">{row.automation_level ? (CRITICALITY_LABELS[row.automation_level?.toUpperCase()] || row.automation_level) : '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{row.assessment_date ? new Date(row.assessment_date).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


function PostureReport({ data, onExport, exporting }) {
  const labels = useLabels();
  const t = useT();
  const postureRows = data.oes_posture_data || [];
  const avgByFn = data.average_by_function || {};
  // Derive domain names from data (framework-agnostic)
  const domainNames = useMemo(() => {
    if (Object.keys(avgByFn).length > 0) return Object.keys(avgByFn);
    const first = postureRows.find(r => r.by_function && Object.keys(r.by_function).length > 0);
    return first ? Object.keys(first.by_function) : ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];
  }, [avgByFn, postureRows]);
  const radarData = useMemo(() => {
    const fns = Object.keys(avgByFn).length > 0 ? Object.keys(avgByFn) : ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];
    return fns.map(fn => ({ function: fn, average: avgByFn[fn] || 0, fullMark: 100 }));
  }, [avgByFn]);

  return (
    <div className="space-y-6">
      <SectionHeader title={t('admin.postureCompliance')}>
        <ExportButton onClick={() => onExport('posture')} loading={exporting} />
      </SectionHeader>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={`${labels.entityName} ${t('admin.assessed')}`} value={data.assessed_posture || 0} />
        <StatCard label={t('dashboard.avgCompliance')} value={`${data.average_compliance || 0}%`} color={data.average_compliance >= 60 ? 'text-green-600' : 'text-orange-500'} />
        <StatCard label={t('common.pending')} value={(data.total_oes || 0) - (data.assessed_posture || 0)} color="text-orange-500" />
        <StatCard label={t('admin.completion')} value={data.total_oes ? `${Math.round((data.assessed_posture / data.total_oes) * 100)}%` : '0%'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Radar */}
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Average Compliance by Function (NIST CSF)</h4>
          {radarData.some(d => d.average > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsRadar cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="function" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar name="Avg Compliance" dataKey="average" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} />
                <Tooltip formatter={(val) => `${val}%`} />
              </RechartsRadar>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No posture data available</p>}
        </Card>

        {/* Compliance bands */}
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('admin.complianceDistribution')}</h4>
          {(data.compliance_distribution || []).some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBar data={(data.compliance_distribution || []).map((d, i) => ({ name: d.band, count: d.count, color: COMPLIANCE_COLORS[i] }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name={`${labels.entityName} Count`} radius={[4, 4, 0, 0]}>
                  {(data.compliance_distribution || []).map((_, i) => <Cell key={i} fill={COMPLIANCE_COLORS[i % COMPLIANCE_COLORS.length]} />)}
                </Bar>
              </RechartsBar>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </Card>
      </div>

      {/* Per-OES table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{labels.entityName} Name</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Overall</th>
                {domainNames.map(fn => (
                  <th key={fn} className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{fn}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {postureRows.length === 0 ? (
                <tr><td colSpan={2 + domainNames.length} className="px-4 py-8 text-center text-gray-400">No posture assessments completed yet</td></tr>
              ) : postureRows.map((row) => (
                <tr key={row.oes_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.oes_name}</td>
                  <td className="px-4 py-3 text-center">
                    <ComplianceBadge value={row.overall_compliance} />
                  </td>
                  {domainNames.map(fn => (
                    <td key={fn} className="px-4 py-3 text-center text-sm text-gray-600">
                      {row.by_function?.[fn] != null ? `${row.by_function[fn]}%` : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ComplianceBadge({ value }) {
  const color = value >= 75 ? 'bg-green-100 text-green-800' : value >= 50 ? 'bg-yellow-100 text-yellow-800' : value >= 25 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${color}`}>{value}%</span>;
}


function RiskReport({ data, onExport, exporting }) {
  const labels = useLabels();
  const t = useT();
  const topThreats = data.top_threats || [];
  const assetExposure = data.asset_exposure || [];
  const topVulns = data.top_vulnerabilities || [];

  return (
    <div className="space-y-6">
      <SectionHeader title={t('admin.riskAssessment')}>
        <ExportButton onClick={() => onExport('risk')} loading={exporting} />
      </SectionHeader>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('admin.maxRiskScore')} value={data.max_risk_score || 0} color={data.max_risk_score >= 15 ? 'text-red-600' : data.max_risk_score >= 10 ? 'text-orange-500' : 'text-green-600'} />
        <StatCard label={t('admin.avgRiskScore')} value={data.average_risk_score || 0} />
        <StatCard label={t('admin.topThreats')} value={topThreats.length} />
        <StatCard label={t('admin.exposedAssets')} value={assetExposure.length} />
      </div>

      {/* Risk distribution + Top threats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('admin.riskClassification')}</h4>
          {(data.risk_distribution || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <RechartsBar data={(data.risk_distribution || []).map(d => ({
                name: d.classification,
                count: d.count,
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]}>
                  {(data.risk_distribution || []).map((d, i) => <Cell key={i} fill={RISK_COLORS[d.classification] || '#94a3b8'} />)}
                </Bar>
              </RechartsBar>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No risk data</p>}
        </Card>

        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t('admin.topThreatsByRisk')}</h4>
          {topThreats.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <RechartsBar data={topThreats.slice(0, 8).map(t => ({ name: t.threat_name?.length > 25 ? t.threat_name.substring(0, 22) + '...' : t.threat_name, risk: t.max_risk, fullName: t.threat_name }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip content={({ payload }) => payload?.[0] ? (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
                    <p className="font-medium">{payload[0].payload.fullName}</p>
                    <p className="text-gray-500">Max Risk: {payload[0].value}</p>
                  </div>
                ) : null} />
                <Bar dataKey="risk" name="Max Risk" fill="#f97316" radius={[0, 4, 4, 0]} />
              </RechartsBar>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No threat data</p>}
        </Card>
      </div>

      {/* Asset exposure + Top vulnerabilities tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{t('admin.assetExposure')}</h4>
          {assetExposure.length > 0 ? (
            <div className="space-y-2">
              {assetExposure.slice(0, 10).map((a, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate mr-3">{a.asset_name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(a.max_risk / 25 * 100, 100)}%`, backgroundColor: a.max_risk >= 15 ? '#dc2626' : a.max_risk >= 10 ? '#f97316' : a.max_risk >= 5 ? '#eab308' : '#22c55e' }} />
                    </div>
                    <span className="text-xs font-mono font-bold text-gray-500 w-8 text-right">{a.max_risk}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No asset data</p>}
        </Card>

        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{t('admin.topVulnerabilities')}</h4>
          {topVulns.length > 0 ? (
            <div className="space-y-2">
              {topVulns.slice(0, 10).map((v, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate mr-3">{v.title}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                    v.classification === 'Critical' ? 'bg-red-100 text-red-800' :
                    v.classification === 'High' ? 'bg-orange-100 text-orange-800' :
                    v.classification === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {v.classification}
                  </span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No vulnerability data</p>}
        </Card>
      </div>
    </div>
  );
}


function ComparativeReport({ data }) {
  const labels = useLabels();
  const t = useT();
  const postureRows = data.oes_posture_data || [];
  const critRows = data.oes_criticality_rows || [];

  // Build a merged dataset for cross-OES comparison
  const merged = useMemo(() => {
    const map = {};
    for (const c of critRows) {
      map[c.oes_id] = { ...c };
    }
    for (const p of postureRows) {
      if (map[p.oes_id]) {
        map[p.oes_id].overall_compliance = p.overall_compliance;
        map[p.oes_id].by_function = p.by_function;
      } else {
        map[p.oes_id] = { oes_id: p.oes_id, oes_name: p.oes_name, overall_compliance: p.overall_compliance, by_function: p.by_function };
      }
    }
    return Object.values(map).filter(r => r.overall_compliance != null || r.criticality);
  }, [critRows, postureRows]);

  // Chart data: compliance vs criticality
  const critOrder = { MA: 5, A: 4, M: 3, B: 2, MB: 1 };
  const comparisonData = useMemo(() =>
    merged
      .filter(r => r.overall_compliance != null)
      .sort((a, b) => (b.overall_compliance || 0) - (a.overall_compliance || 0))
      .map(r => ({
        name: r.oes_name?.length > 18 ? r.oes_name.substring(0, 15) + '...' : r.oes_name,
        fullName: r.oes_name,
        compliance: r.overall_compliance || 0,
        criticality: critOrder[r.criticality?.toUpperCase()] || 0,
      })),
    [merged]
  );

  // Radar comparison: pick top 6 OES with posture data
  const radarOes = useMemo(() => postureRows.slice(0, 6), [postureRows]);
  const compDomainNames = useMemo(() => {
    const first = postureRows.find(r => r.by_function && Object.keys(r.by_function).length > 0);
    return first ? Object.keys(first.by_function) : ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];
  }, [postureRows]);
  const radarData = useMemo(() => {
    const fns = compDomainNames;
    return fns.map(fn => {
      const entry = { function: fn };
      for (const oes of radarOes) {
        entry[oes.oes_name] = oes.by_function?.[fn] || 0;
      }
      return entry;
    });
  }, [radarOes]);

  const radarColors = ['#3b82f6', '#f97316', '#22c55e', '#8b5cf6', '#ec4899', '#14b8a6'];

  return (
    <div className="space-y-6">
      <SectionHeader title={`Cross-${labels.entityName} Comparative Analysis`} />

      {/* Compliance ranking */}
      <Card className="p-5">
        <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Compliance vs Criticality</h4>
        {comparisonData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(300, comparisonData.length * 32)}>
            <RechartsBar data={comparisonData} layout="vertical" barSize={14} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <Tooltip content={({ payload }) => payload?.[0] ? (
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2.5 text-xs">
                  <p className="font-semibold text-slate-800">{payload[0].payload.fullName}</p>
                  <p className="text-blue-600 mt-1">Compliance: {payload[0].payload.compliance}%</p>
                  <p className="text-orange-600">Criticality: {CRITICALITY_LABELS[Object.keys(critOrder).find(k => critOrder[k] === payload[0].payload.criticality)] || 'N/A'}</p>
                </div>
              ) : null} />
              <Bar dataKey="compliance" name="Compliance %" fill="#3b82f6" radius={[0, 6, 6, 0]} />
            </RechartsBar>
          </ResponsiveContainer>
        ) : <p className="text-sm text-slate-400 text-center py-8">Need at least one {labels.entityName} with posture data for comparison</p>}
      </Card>

      {/* Multi-OES radar */}
      {radarOes.length > 1 && (
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Multi-{labels.entityName} Posture Radar</h4>
          <ResponsiveContainer width="100%" height={400}>
            <RechartsRadar cx="50%" cy="50%" outerRadius="70%" data={radarData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="function" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              {radarOes.map((oes, i) => (
                <Radar key={oes.oes_id} name={oes.oes_name} dataKey={oes.oes_name} stroke={radarColors[i % radarColors.length]} fill={radarColors[i % radarColors.length]} fillOpacity={0.1} />
              ))}
              <Legend />
              <Tooltip formatter={(val) => `${val}%`} />
            </RechartsRadar>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Summary table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{labels.entityName} Name</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Sector</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Criticality</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Compliance</th>
                {compDomainNames.map(fn => (
                  <th key={fn} className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{fn.substring(0, 3)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {merged.length === 0 ? (
                <tr><td colSpan={4 + compDomainNames.length} className="px-4 py-8 text-center text-gray-400">No data available for comparison</td></tr>
              ) : merged.map((row) => (
                <tr key={row.oes_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.oes_name}</td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">{row.sector || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {row.criticality ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: CRITICALITY_COLORS[row.criticality?.toUpperCase()] || '#94a3b8' }}>
                        {CRITICALITY_LABELS[row.criticality?.toUpperCase()] || row.criticality}
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.overall_compliance != null ? <ComplianceBadge value={row.overall_compliance} /> : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  {compDomainNames.map(fn => (
                    <td key={fn} className="px-4 py-3 text-center text-xs text-gray-600">
                      {row.by_function?.[fn] != null ? `${row.by_function[fn]}%` : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


// ─── Main Reports component ─────────────────────────────────────────

export default function Reports() {
  const labels = useLabels();
  const t = useT();
  const { pureOES } = useInstanceMode();
  const TAB_LABELS = {
    executive: t('reports.executiveSummary'),
    criticality: t('reports.criticality'),
    posture: t('reports.posture'),
    risk: t('reports.risk'),
    comparative: t('reports.comparative'),
    dependencies: t('reports.dependencies'),
  };
  const TABS = [
    ...BASE_TAB_KEYS.map(key => ({ key, label: TAB_LABELS[key] })),
    ...(labels.showSectorDeps ? [{ key: 'dependencies', label: TAB_LABELS.dependencies }] : []),
  ];
  const [myOesId, setMyOesId] = useState(null);
  const [activeTab, setActiveTab] = useState('executive');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingFullReport, setGeneratingFullReport] = useState(false);
  const [sectorDeps, setSectorDeps] = useState(null);
  const [anonymized, setAnonymized] = useState(false);

  // Anonymized or raw data for display
  const displayData = useMemo(() => {
    if (!data) return null;
    return anonymized ? anonymizeReport(data) : data;
  }, [data, anonymized]);

  // Assessment filter
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState('');

  // Load assessments list for filter (exclude Default Assessment placeholder)
  useEffect(() => {
    assessmentApi.getAll(0, 200, false)
      .then(res => setAssessments((res.data || []).filter(a => a.name !== 'Default Assessment')))
      .catch(() => {});
    // Load sector dependency configuration
    reportsApi.getSummary().catch(() => {}); // warm cache
    settingsApi.getSectorDeps()
      .then(res => setSectorDeps(res.data?.matrix || null))
      .catch(() => {});
  }, []);

  // In OES mode, find the single OES ID for filtering
  useEffect(() => {
    if (pureOES) {
      oesApi.getAll().then(r => {
        const list = r.data || [];
        if (list.length > 0) setMyOesId(list[0].id);
      }).catch(() => {});
    }
  }, [pureOES]);

  // Filter report data to single OES in OES mode
  const filterForOES = (reportData, oesId) => {
    if (!reportData || !oesId) return reportData;
    const filtered = { ...reportData };
    if (filtered.oes_criticality_rows) filtered.oes_criticality_rows = filtered.oes_criticality_rows.filter(r => r.oes_id === oesId);
    if (filtered.oes_posture_data) filtered.oes_posture_data = filtered.oes_posture_data.filter(r => r.oes_id === oesId);
    if (filtered.oes_details) filtered.oes_details = filtered.oes_details.filter(r => r.oes_id === oesId);
    filtered.total_oes = filtered.oes_criticality_rows?.length || 0;
    filtered.assessed_criticality = filtered.oes_criticality_rows?.length || 0;
    filtered.assessed_posture = filtered.oes_posture_data?.length || 0;
    return filtered;
  };

  // Load report data
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const assessmentId = selectedAssessment || null;
      const res = await reportsApi.getSummary(assessmentId);
      const reportData = pureOES && myOesId ? filterForOES(res.data, myOesId) : res.data;
      setData(reportData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [selectedAssessment, pureOES, myOesId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Export handler
  const handleExport = async (type) => {
    try {
      setExporting(true);
      const assessmentId = selectedAssessment || null;
      let res;
      if (type === 'criticality') res = await reportsApi.exportCriticality(assessmentId);
      else if (type === 'posture') res = await reportsApi.exportPosture(assessmentId);
      else if (type === 'risk') res = await reportsApi.exportRisk(assessmentId);
      if (res) downloadBlob(new Blob([res.data]), `${type}_report.csv`);
    } catch {
      setError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!data) return;
    try {
      setGeneratingPdf(true);
      await generateReportPdf(displayData, null, { anonymized }, labels);
    } catch (err) {
      console.error('PDF generation error:', err);
      setError('PDF generation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadFullReport = async () => {
    try {
      setGeneratingFullReport(true);
      setError(null);
      const assessmentId = selectedAssessment || null;
      const res = await reportsApi.getFullReport(assessmentId);
      const fullData = anonymized ? anonymizeReport(res.data) : res.data;
      await generateFullReportPdf(fullData, null, { anonymized }, labels);
    } catch (err) {
      console.error('Full report generation error:', err);
      setError('Full report generation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setGeneratingFullReport(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-eucnet-navy">{t('nav.reports')}</h1>
            <p className="text-sm text-slate-500 mt-1">{t('admin.comprehensiveReporting')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-52">
              <CustomSelect
                value={selectedAssessment}
                onChange={(e) => setSelectedAssessment(e.target.value)}
                options={[
                  { value: '', label: t('admin.allAssessments') },
                  ...assessments.map(a => ({ value: String(a.id), label: a.name })),
                ]}
                placeholder={t('admin.allAssessments')}
                searchable={assessments.length > 5}
                size="sm"
              />
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <span className="text-xs font-medium text-slate-500">{t('reports.anonymize')}</span>
              <div className="relative">
                <input type="checkbox" className="sr-only peer" checked={anonymized} onChange={() => setAnonymized(v => !v)} />
                <div className="w-9 h-5 bg-slate-200 peer-checked:bg-eucnet-sky rounded-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
              </div>
            </label>
            <button
              onClick={handleDownloadPdf}
              disabled={generatingPdf || !displayData}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-lg bg-eucnet-navy text-white hover:bg-eucnet-blue transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {generatingPdf ? t('reports.generating') : t('reports.summaryPdf')}
            </button>
            <button
              onClick={handleDownloadFullReport}
              disabled={generatingFullReport}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-lg border border-eucnet-navy text-eucnet-navy hover:bg-eucnet-navy hover:text-white transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {generatingFullReport ? t('admin.generatingReport') : t('reports.fullReport')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-0 -mb-px overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-eucnet-sky text-eucnet-sky'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {loading && (
          <Card>
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-eucnet-sky mx-auto mb-4" />
              <p className="text-gray-600">{t('admin.loadingReportData')}</p>
            </div>
          </Card>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
            <button onClick={fetchReport} className="ml-3 text-sm underline">{t('common.retry')}</button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {activeTab === 'executive' && <ExecutiveSummary data={displayData} />}
            {activeTab === 'criticality' && <CriticalityReport data={displayData} onExport={handleExport} exporting={exporting} />}
            {activeTab === 'posture' && <PostureReport data={displayData} onExport={handleExport} exporting={exporting} />}
            {activeTab === 'risk' && <RiskReport data={displayData} onExport={handleExport} exporting={exporting} />}
            {activeTab === 'comparative' && <ComparativeReport data={displayData} />}
            {labels.showSectorDeps && activeTab === 'dependencies' && <DependencyMap data={displayData} sectorDeps={sectorDeps} />}
          </>
        )}
      </div>
    </Layout>
  );
}
